import { DEFAULT_TIMEZONE } from "@/lib/constants";
import { type DateInput, startOfDayInTz, toDayKey } from "@/utils/format-datetime";

type Duration = { hours: number; minutes: number };

export function minutesToDuration(totalMinutes: number): Duration {
  return {
    hours: Math.floor(Math.abs(totalMinutes) / 60),
    minutes: Math.abs(totalMinutes) % 60,
  };
}

export function durationToMinutes(duration: Duration): number {
  return duration.hours * 60 + duration.minutes;
}

export function parseTimeString(timeString: string): Duration {
  if (!timeString) {
    return { hours: 0, minutes: 0 };
  }

  const [hours, minutes] = timeString.split(":").map(Number);

  if (isNaN(hours) || isNaN(minutes)) {
    return { hours: 0, minutes: 0 };
  }

  return { hours: Number(hours), minutes: Number(minutes) };
}

export function formatDurationToTimeString(duration: Duration): string {
  return `${String(duration.hours).padStart(2, "0")}:${String(duration.minutes).padStart(2, "0")}`;
}

export function calculateShiftDuration(
  start: Date | string | number,
  end: Date | string | number,
  breakMinutes: number = 0,
): Duration {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const diffMinutes = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60));
  return minutesToDuration(diffMinutes - breakMinutes);
}

export function formatDurationToISO(duration: Duration): string {
  return `PT${duration.hours}H${duration.minutes}M`;
}

/**
 * Begin van vandaag in projecttijd, als UTC-instant — bedoeld als `gte`-filter op de API.
 *
 * Heette zo omdat het middernacht **UTC** teruggaf. Dat is niet hetzelfde als het begin van de dag
 * in Amsterdam: tussen 00:00 en 02:00 UTC viel een dienst die om 00:30 projecttijd begint daardoor
 * buiten het filter en verdween hij uit de lijst.
 */
export function getUtcStartOfDay(timeZone: string | null = DEFAULT_TIMEZONE): string {
  return startOfDayInTz(new Date(), timeZone).toISOString();
}

/**
 * Sleutel om twee gebeurtenissen (diensten, aanmeldingen) op dezelfde kalenderdag van hetzelfde
 * project te herkennen. Wordt gebruikt om een aanmelding te verbergen zodra er een dienst voor die
 * dag staat.
 *
 * Neemt het ruwe moment, niet een voorbewerkte `Date`. De vorige versie las
 * `getFullYear()/getMonth()/getDate()` van een `tzDate()`-resultaat; die velden staan in de
 * tijdzone van het TOESTEL, waardoor de dag op een telefoon buiten Amsterdam kon verspringen en de
 * deduplicatie stil de verkeerde kaart liet staan.
 *
 * @example
 *   ```typescript
 *   createDayProjectKey("2024-03-15T23:00:00Z", "project-123");
 *   // "2024-03-16-project-123" — 23:00Z is al 16 maart in Amsterdam
 *   ```;
 *
 * @param value - Het moment van de gebeurtenis (ISO-string, Date of epoch)
 * @param projectId - The project ID to include in the key
 * @param timeZone - Tijdzone van het project. Standaard Europe/Amsterdam.
 * @returns A string key in format: "YYYY-MM-DD-projectId"
 */
export function createDayProjectKey(
  value: DateInput,
  projectId: string,
  timeZone: string | null = DEFAULT_TIMEZONE,
): string {
  return `${toDayKey(value, timeZone)}-${projectId}`;
}
